import React from 'react';
import { Link } from 'react-router-dom';
import './Terms.css';

export default function Terms() {
  return (
    <div className="legal-container">
      <div className="legal-glow" />
      
      <div className="legal-card">
        <div className="legal-header">
          <h1>Terms of Use</h1>
          <div className="legal-meta">
            <span className="meta-item">Effective Date: January 1, 2026</span>
            <span className="meta-divider">•</span>
            <span className="meta-item">Last Updated: January 31, 2026</span>
          </div>
          <div className="legal-company">
            <span>APPCORE LABS LIMITED</span>
            <span className="company-location">London, United Kingdom</span>
          </div>
        </div>

        <div className="legal-content">
          <p className="intro">
            These Terms of Use ("Terms", "Agreement") constitute a legally binding agreement between you ("User", "you", "your") and APPCORE LABS LIMITED, a company registered in England and Wales ("Company", "we", "us", "our"), governing your access to and use of our mobile applications distributed through the Apple App Store (collectively, the "Services"). By downloading, installing, or using any of our applications, you acknowledge that you have read, understood, and agree to be bound by these Terms.
          </p>

          <section className="legal-section">
            <h2><span className="section-number">01</span>Acceptance of Terms</h2>
            <div className="section-content">
              <h3>1.1 Agreement to Terms</h3>
              <p>By accessing or using our Services, you agree to comply with and be bound by these Terms. If you do not agree to these Terms, you must not access or use our Services.</p>
              
              <h3>1.2 Eligibility</h3>
              <p>You must be at least 13 years of age (or the minimum age of digital consent in your jurisdiction) to use our Services. By using our Services, you represent and warrant that you meet the applicable age requirements and have the legal capacity to enter into this Agreement.</p>
              
              <h3>1.3 Updates to Terms</h3>
              <p>We reserve the right to modify these Terms at any time. We will provide notice of material changes by updating the "Last Updated" date. Your continued use of the Services following any changes constitutes acceptance of the revised Terms.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">02</span>License and Use Rights</h2>
            <div className="section-content">
              <h3>2.1 Limited License</h3>
              <p>Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to download, install, and use our applications on compatible devices that you own or control, solely for your personal, non-commercial purposes.</p>
              
              <h3>2.2 Restrictions</h3>
              <p>You agree not to: copy, modify, or distribute the applications or any content therein; reverse engineer, decompile, or disassemble any part of our applications; remove or alter any proprietary notices or labels; use the Services for any illegal, harmful, or unauthorized purpose; attempt to gain unauthorized access to our systems or networks; or interfere with the proper functioning of our Services.</p>
              
              <h3>2.3 Third-Party Services</h3>
              <p>Our applications may contain links to or integrate with third-party services. We are not responsible for the content, privacy practices, or terms of any third-party services.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">03</span>Intellectual Property Rights</h2>
            <div className="section-content">
              <h3>3.1 Ownership</h3>
              <p>All content, features, functionality, and underlying technology of our Services, including but not limited to text, graphics, logos, icons, images, audio, video, software, and code, are the exclusive property of APPCORE LABS LIMITED or our licensors and are protected by international copyright, trademark, patent, and other intellectual property laws.</p>
              
              <h3>3.2 Trademarks</h3>
              <p>Our company name, logos, product names, and all related marks are trademarks of APPCORE LABS LIMITED. You may not use any of our trademarks without our prior written consent.</p>
              
              <h3>3.3 Feedback</h3>
              <p>If you provide us with any feedback, suggestions, or ideas regarding our Services, you grant us an unrestricted, perpetual, irrevocable, royalty-free license to use such feedback for any purpose without compensation or attribution to you.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">04</span>User Content and Conduct</h2>
            <div className="section-content">
              <h3>4.1 User Responsibilities</h3>
              <p>If our applications allow you to submit, upload, or share content ("User Content"), you are solely responsible for the legality, accuracy, and appropriateness of such content. You represent and warrant that you own or have the necessary rights to use and share any User Content.</p>
              
              <h3>4.2 Prohibited Content</h3>
              <p>You agree not to submit any content that: infringes any intellectual property or other rights; contains viruses or malicious code; is defamatory, obscene, or offensive; violates any applicable law or regulation; or impersonates any person or entity.</p>
              
              <h3>4.3 Content Moderation</h3>
              <p>We reserve the right, but have no obligation, to monitor, review, and remove any User Content at our sole discretion, without notice, for any reason, including violation of these Terms.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">05</span>Subscription and Payments</h2>
            <div className="section-content">
              <h3>5.1 In-App Purchases</h3>
              <p>Some features of our applications may require payment or subscription. All purchases are processed through the Apple App Store and are subject to Apple's terms and conditions.</p>
              
              <h3>5.2 Subscription Terms</h3>
              <p>Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period. You can manage and cancel subscriptions through your App Store account settings.</p>
              
              <h3>5.3 Refunds</h3>
              <p>All payments are non-refundable except as required by applicable law or as expressly stated in these Terms. Refund requests must be directed to Apple in accordance with their refund policies.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">06</span>Disclaimer of Warranties</h2>
            <div className="section-content">
              <p>THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.</p>
              <p>We do not warrant that the Services will be uninterrupted, error-free, secure, or free of viruses or other harmful components. We make no representations regarding the accuracy, reliability, or completeness of any content available through our Services.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">07</span>Limitation of Liability</h2>
            <div className="section-content">
              <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL APPCORE LABS LIMITED, ITS DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH YOUR ACCESS TO OR USE OF (OR INABILITY TO ACCESS OR USE) THE SERVICES.</p>
              <p>OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID TO US, IF ANY, DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">08</span>Indemnification</h2>
            <div className="section-content">
              <p>You agree to indemnify, defend, and hold harmless APPCORE LABS LIMITED and its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, losses, costs, and expenses (including reasonable legal fees) arising out of or related to: your use of the Services; your violation of these Terms; your violation of any rights of a third party; or any User Content you submit or share.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">09</span>Termination</h2>
            <div className="section-content">
              <h3>9.1 Termination by Us</h3>
              <p>We may suspend or terminate your access to the Services immediately, without prior notice or liability, for any reason, including if we believe you have violated these Terms.</p>
              
              <h3>9.2 Termination by You</h3>
              <p>You may terminate this Agreement at any time by uninstalling our applications and ceasing all use of our Services.</p>
              
              <h3>9.3 Effect of Termination</h3>
              <p>Upon termination, your right to use the Services will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnification, and limitations of liability.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">10</span>Governing Law and Disputes</h2>
            <div className="section-content">
              <h3>10.1 Governing Law</h3>
              <p>These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law principles.</p>
              
              <h3>10.2 Dispute Resolution</h3>
              <p>Any dispute arising out of or relating to these Terms or the Services shall first be attempted to be resolved through good faith negotiations. If the dispute cannot be resolved amicably within thirty (30) days, either party may submit the dispute to the exclusive jurisdiction of the courts of England and Wales.</p>
              
              <h3>10.3 Class Action Waiver</h3>
              <p>To the fullest extent permitted by applicable law, you agree to resolve any disputes with us on an individual basis and waive any right to participate in a class action lawsuit or class-wide arbitration.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">11</span>General Provisions</h2>
            <div className="section-content">
              <h3>11.1 Entire Agreement</h3>
              <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and APPCORE LABS LIMITED regarding your use of the Services and supersede all prior agreements and understandings.</p>
              
              <h3>11.2 Severability</h3>
              <p>If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect.</p>
              
              <h3>11.3 Waiver</h3>
              <p>Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.</p>
              
              <h3>11.4 Assignment</h3>
              <p>You may not assign or transfer these Terms or your rights hereunder without our prior written consent. We may assign these Terms without restriction.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">12</span>Contact Information</h2>
            <div className="section-content">
              <p>For any questions, concerns, or notices regarding these Terms of Use, please contact us:</p>
              <div className="contact-info">
                <p><strong>APPCORE LABS LIMITED</strong></p>
                <p>Unit 501, Leroy House</p>
                <p>London, N1 3FY</p>
                <p>United Kingdom</p>
                <p>Email: <a href="mailto:hello@appcorelab.co">hello@appcorelab.co</a></p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Link to="/" className="back-btn">
        <span className="back-arrow">←</span>
        Back to Home
      </Link>
    </div>
  );
}